import React, { useCallback } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { useMobileDragResize } from '../hooks/useMobileDragResize';

/**
 * Reusable Sidebar Wrapper with drag-to-resize and separate close button
 *
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the device is mobile
 * @param {Object} props.sidebarConfig - Sidebar configuration (width, height, etc.)
 * @param {Function} props.onClose - Callback when close button is clicked
 * @param {Function} props.onHeightChange - Callback when height changes (for map resize)
 * @param {number} props.zIndex - Z-index for the sidebar (default: 500)
 * @param {number} props.minHeightVh - Minimum height in vh for mobile (default: 20)
 * @param {number} props.maxHeightVh - Maximum height in vh for mobile (default: 90)
 * @param {React.ReactNode} props.children - Sidebar content
 */
export default function SidebarWrapper({
  isMobile,
  sidebarConfig,
  onClose,
  onHeightChange,
  zIndex = 500,
  isVisible = true,
  onToggleVisibility = null,
  panelLabel = 'Panel',
  minHeightVh = 20,
  maxHeightVh = 90,
  showCloseButton = true,
  showVisibilityHandle = true,
  children,
}) {
  const { height: dragHeight, dragHandleProps } = useMobileDragResize(
    isMobile,
    sidebarConfig?.height || '50vh',
    minHeightVh,
    maxHeightVh,
    onHeightChange
  );
  const sidebarWidthPx = Number.parseInt(sidebarConfig?.width || '400', 10) || 400;
  const canToggleVisibility = typeof onToggleVisibility === 'function';

  const handleVisibilityChange = useCallback((nextValue) => {
    if (canToggleVisibility) {
      onToggleVisibility(nextValue);
      return;
    }

    if (nextValue === false && typeof onClose === 'function') {
      onClose();
    }
  }, [canToggleVisibility, onClose, onToggleVisibility]);

  const handleToggleClick = useCallback((event) => {
    dragHandleProps?.onClick?.(event);
    if (event?.defaultPrevented) return;
    handleVisibilityChange(!isVisible);
  }, [dragHandleProps, handleVisibilityChange, isVisible]);

  if (!isVisible && !canToggleVisibility) {
    return null;
  }

  const handleBaseStyle = {
    background: '#ffffff',
    border: '1px solid #dbe3ee',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
    color: '#334155',
    cursor: 'pointer',
  };
  const dotGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 4px)',
    gridTemplateRows: 'repeat(2, 4px)',
    gap: '4px 4px',
    alignItems: 'center',
    justifyItems: 'center',
  };
  const dotStyle = {
    width: '4px',
    height: '4px',
    borderRadius: '999px',
    background: '#94a3b8',
  };

  const renderHandleContent = () => {
    return (
      <span style={dotGridStyle} aria-hidden="true">
        {Array.from({ length: 8 }).map((_, index) => (
          <span key={index} style={dotStyle} />
        ))}
      </span>
    );
  };

  const renderVisibilityHandle = () => {
    if (!canToggleVisibility || !showVisibilityHandle) return null;

    if (isMobile) {
      const dragProps = isVisible
        ? {
            onTouchStart: dragHandleProps?.onTouchStart,
            onMouseDown: dragHandleProps?.onMouseDown,
            style: dragHandleProps?.style,
          }
        : {};

      return (
        <button
          data-scroll-overlay="true"
          type="button"
          aria-label={isVisible ? `Hide ${panelLabel}` : `Open ${panelLabel}`}
          onClick={handleToggleClick}
          {...dragProps}
          style={{
            ...handleBaseStyle,
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: isVisible ? `calc(${dragHeight} - 18px)` : '10px',
            zIndex: zIndex + 2,
            width: '58px',
            height: '34px',
            borderRadius: '999px',
            padding: '0',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: isVisible ? 'none' : 'auto',
            userSelect: 'none',
          }}
        >
          {renderHandleContent()}
        </button>
      );
    }

    return (
      <button
        data-scroll-overlay="true"
        type="button"
        aria-label={isVisible ? `Hide ${panelLabel}` : `Open ${panelLabel}`}
        onClick={() => handleVisibilityChange(!isVisible)}
        title={isVisible ? `Hide ${panelLabel}` : `Open ${panelLabel}`}
        style={{
          ...handleBaseStyle,
          position: 'fixed',
          right: isVisible ? `${Math.max(sidebarWidthPx - 18, 0)}px` : '0px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '34px',
          height: '64px',
          borderRadius: '999px',
          padding: '0',
          zIndex: zIndex + 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {renderHandleContent()}
      </button>
    );
  };

  return (
    <>
      {renderVisibilityHandle()}

      {isVisible && (
        <div
          data-scroll-overlay="true"
          style={{
            zIndex,
            width: isMobile ? '100%' : sidebarConfig?.width || '400px',
            height: isMobile ? dragHeight : sidebarConfig?.height || '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            bottom: isMobile ? 0 : 'auto',
            left: isMobile ? 0 : 'auto',
            right: isMobile ? 0 : 0,
            top: isMobile ? 'auto' : 0,
            background: '#ffffff',
            boxShadow: isMobile ? '0 -12px 30px rgba(15, 23, 42, 0.18)' : 'none',
            borderRadius: isMobile ? '20px 20px 0 0' : '0',
            overflow: 'hidden',
          }}
        >
          {showCloseButton && (
            <Button
              id="close"
              onClick={onClose}
              circular
              icon
              style={{
                position: 'absolute',
                right: '10px',
                top: '10px',
                background: '#ffffff',
                border: '1px solid #dbe3ee',
                width: '34px',
                height: '34px',
                padding: '0',
                zIndex: 9999,
                boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="close" style={{ margin: 0, fontSize: '1em', color: '#475569' }} />
            </Button>
          )}

          <div
            data-scroll-overlay="true"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              paddingTop: isMobile && canToggleVisibility && showVisibilityHandle ? '22px' : '0',
            }}
          >
            {children}
          </div>
        </div>
      )}
    </>
  );
}
